/**
 * ReplayRecorder — captures a replayable session by hooking into the
 * UndoManager and sampling the editor camera over time.
 *
 * Lifecycle:
 *   const recorder = new ReplayRecorder(traceScene, undoManager);
 *   recorder.start();           // takes initial scene + camera snapshot
 *   ... user does stuff ...
 *   const session = recorder.stop();  // returns a ReplaySession
 *   // session can be JSON.stringify'd and saved as a .replay.json
 *
 * The recorder is deliberately minimal — it does NOT attempt to capture
 * non-undo-tracked side effects (asset uploads, CMS saves, play-mode state
 * changes, etc.). Commands that can't `serialize()` are recorded as a
 * marker event so the UI can warn the user.
 */

import type { TraceScene } from '../../core/TraceScene';
import type { UndoManager } from '../undo/UndoManager';
import type { UndoCommand } from '../undo/UndoCommand';
import type { SceneObjectData } from '../../scenedata/sceneobjectdata';
import {
    type ReplaySession,
    type ReplayEvent,
    type CameraPose,
    REPLAY_SESSION_VERSION,
} from './ReplaySession';

/** Default camera sample throttle — 100ms (10Hz) is more than enough for lerped playback. */
const DEFAULT_CAMERA_SAMPLE_MS = 100;

/**
 * Minimum movement to emit a new camera sample. Avoids flooding the timeline
 * with identical poses when the user isn't touching the camera. Values are
 * generous — tiny FP jitter from Babylon's camera interpolation shouldn't
 * produce events.
 */
const CAMERA_EPSILON = {
    angular: 1e-4,   // radians (alpha/beta)
    radius: 1e-4,
    target: 1e-4,
};

export interface ReplayRecorderOptions {
    /** Override camera sampling throttle (ms). Defaults to 100ms. */
    cameraSampleIntervalMs?: number;
    /** Disable camera sampling entirely (useful for tests / headless). */
    disableCameraSampling?: boolean;
}

export class ReplayRecorder {
    private traceScene: TraceScene;
    private undoManager: UndoManager;
    private options: Required<ReplayRecorderOptions>;

    private recording = false;
    private startTime = 0;
    private events: ReplayEvent[] = [];
    private initialScene: SceneObjectData[] | null = null;
    private initialCamera: CameraPose | null = null;

    private unsubscribePush: (() => void) | null = null;
    private cameraIntervalId: ReturnType<typeof setInterval> | null = null;
    private lastCameraPose: CameraPose | null = null;

    constructor(traceScene: TraceScene, undoManager: UndoManager, options: ReplayRecorderOptions = {}) {
        this.traceScene = traceScene;
        this.undoManager = undoManager;
        this.options = {
            cameraSampleIntervalMs: options.cameraSampleIntervalMs ?? DEFAULT_CAMERA_SAMPLE_MS,
            disableCameraSampling: options.disableCameraSampling ?? false,
        };
    }

    get isRecording(): boolean {
        return this.recording;
    }

    /** Current event count (for UI status readout). */
    get eventCount(): number {
        return this.events.length;
    }

    /**
     * Begin recording. Captures initial scene + camera state immediately,
     * then subscribes to UndoManager pushes and starts sampling the camera.
     */
    start(): void {
        if (this.recording) return;
        this.recording = true;
        this.startTime = performance.now();
        this.events = [];

        this.initialScene = structuredClone(this.traceScene.getSceneDataList());
        this.initialCamera = this.captureCameraPose();
        this.lastCameraPose = this.initialCamera;

        this.unsubscribePush = this.undoManager.subscribePush((cmd, coalesced) => {
            this.onCommandPushed(cmd, coalesced);
        });

        if (!this.options.disableCameraSampling) {
            this.cameraIntervalId = setInterval(
                () => this.sampleCamera(),
                this.options.cameraSampleIntervalMs,
            );
        }
    }

    /**
     * Stop recording and produce a session. Safe to call if not recording
     * (returns an empty session rooted at whatever initial state was
     * captured, or a fully empty session if never started).
     */
    stop(): ReplaySession {
        if (this.unsubscribePush) {
            this.unsubscribePush();
            this.unsubscribePush = null;
        }
        if (this.cameraIntervalId !== null) {
            clearInterval(this.cameraIntervalId);
            this.cameraIntervalId = null;
        }
        this.recording = false;

        const events = [...this.events].sort((a, b) => a.t - b.t);
        return {
            version: REPLAY_SESSION_VERSION,
            createdAt: Date.now(),
            initialScene: this.initialScene ?? [],
            initialCamera: this.initialCamera ?? { alpha: 0, beta: 0, radius: 1, target: [0, 0, 0] },
            events,
            duration: events.length > 0 ? events[events.length - 1].t : 0,
        };
    }

    /**
     * Discard any in-progress recording without producing a session.
     * Detaches subscriptions just like `stop()`.
     */
    cancel(): void {
        if (this.unsubscribePush) {
            this.unsubscribePush();
            this.unsubscribePush = null;
        }
        if (this.cameraIntervalId !== null) {
            clearInterval(this.cameraIntervalId);
            this.cameraIntervalId = null;
        }
        this.recording = false;
        this.events = [];
        this.initialScene = null;
        this.initialCamera = null;
        this.lastCameraPose = null;
    }

    private onCommandPushed(cmd: UndoCommand, coalesced: boolean): void {
        const t = performance.now() - this.startTime;

        if (coalesced) {
            // Coalesce replaces the on-stack command's `after` state with a
            // newer one. The prior recorded event is now out of date — re-
            // serializing the coalesced command and overwriting the most
            // recent command event of the same label keeps the recording
            // aligned with what undo/redo will actually do. Drop the old
            // entry rather than stacking duplicates.
            this.replaceLastCommandEvent(cmd, t);
            return;
        }

        if (typeof cmd.serialize === 'function') {
            this.events.push({
                kind: 'command',
                t,
                label: cmd.label,
                payload: cmd.serialize(),
            });
        } else {
            // Commands without a codec (e.g. ActionCommand with closures)
            // leave a marker so the player can report a gap rather than
            // silently diverging.
            this.events.push({
                kind: 'marker',
                t,
                note: `Non-replayable: ${cmd.label}`,
            });
        }
    }

    private replaceLastCommandEvent(cmd: UndoCommand, t: number): void {
        // Walk backwards to the most recent command event and overwrite its
        // payload with the current (post-coalesce) serialization. If there
        // is no prior command event, treat this like a fresh push.
        for (let i = this.events.length - 1; i >= 0; i--) {
            if (this.events[i].kind === 'command') {
                if (typeof cmd.serialize === 'function') {
                    this.events[i] = {
                        kind: 'command',
                        t,
                        label: cmd.label,
                        payload: cmd.serialize(),
                    };
                }
                return;
            }
        }
        // Fall through: no prior command event, record as a new one.
        if (typeof cmd.serialize === 'function') {
            this.events.push({ kind: 'command', t, label: cmd.label, payload: cmd.serialize() });
        }
    }

    private sampleCamera(): void {
        const pose = this.captureCameraPose();
        if (!pose) return;
        if (this.lastCameraPose && poseApproxEqual(pose, this.lastCameraPose)) return;
        const t = performance.now() - this.startTime;
        this.events.push({ kind: 'camera', t, pose });
        this.lastCameraPose = pose;
    }

    private captureCameraPose(): CameraPose | null {
        const cam = this.traceScene.getCamera();
        if (!cam) return null;
        return {
            alpha: cam.alpha,
            beta: cam.beta,
            radius: cam.radius,
            target: [cam.target.x, cam.target.y, cam.target.z],
        };
    }
}

function poseApproxEqual(a: CameraPose, b: CameraPose): boolean {
    return (
        Math.abs(a.alpha - b.alpha) < CAMERA_EPSILON.angular &&
        Math.abs(a.beta - b.beta) < CAMERA_EPSILON.angular &&
        Math.abs(a.radius - b.radius) < CAMERA_EPSILON.radius &&
        Math.abs(a.target[0] - b.target[0]) < CAMERA_EPSILON.target &&
        Math.abs(a.target[1] - b.target[1]) < CAMERA_EPSILON.target &&
        Math.abs(a.target[2] - b.target[2]) < CAMERA_EPSILON.target
    );
}
